import React from "react";
import { FaTrash, FaDollarSign } from "react-icons/fa";
import {
  RECEIPT_FILE_ACCEPT,
  isReceiptImageFile,
  isReceiptPdfFile,
} from "../../utilis/financeInvoicePaymentHelpers";
import {
  normalizeInvoiceAmountInput,
  sanitizeDecimalInput,
} from "../../utilis/invoiceLineItemHelpers";

function ApplicationPaymentForm({
  payment = {},
  onPaymentChange,
  receiptFile = null,
  receiptPreview = "",
  onReceiptChange,
  onReceiptRemove,
  onDrop,
  onDragOver,
  creditsAvailable = null,
  remainingBalance = 0,
  loading = false,
  onSubmit,
  onCancel,
  uploadInputId = "applicationPaymentReceipt",
  title = "Add Payment",
  compact = false,
}) {
  const disabled = remainingBalance <= 0;
  const isCreditMethod = payment.method === "overpayment_credit";
  const canSubmit =
    !loading && !disabled && (isCreditMethod || parseFloat(payment.amount) > 0);

  return (
    <div
      className={`application-payment-form${
        compact ? " application-payment-form--compact" : ""
      }`}
    >
      <div className="application-payment-form__header">
        <div className="application-payment-form__title">{title}</div>
        {disabled && (
          <small className="application-payment-form__hint">
            No remaining amount to pay
          </small>
        )}
      </div>

      <div className="application-payment-form__fields">
        {!isCreditMethod && (
          <div className="application-payment-form__field">
            <label className="form-label">Amount</label>
            <input
              type="number"
              min="0"
              step="0.0001"
              value={payment.amount ?? ""}
              onChange={(e) =>
                onPaymentChange?.(
                  "amount",
                  sanitizeDecimalInput(e.target.value),
                )
              }
              onBlur={(e) =>
                onPaymentChange?.(
                  "amount",
                  normalizeInvoiceAmountInput(e.target.value),
                )
              }
              className="form-control"
              placeholder="e.g. 250.00"
              disabled={disabled}
            />
          </div>
        )}

        <div className="application-payment-form__field">
          <label className="form-label">Date</label>
          <input
            type="date"
            value={payment.payment_date ?? ""}
            max={new Date().toISOString().split("T")[0]}
            onChange={(e) => onPaymentChange?.("payment_date", e.target.value)}
            className="form-control"
            disabled={disabled}
          />
        </div>

        <div className="application-payment-form__field">
          <label className="form-label">Method</label>
          <select
            value={payment.method ?? "cash"}
            onChange={(e) => onPaymentChange?.("method", e.target.value)}
            className="form-select"
            disabled={loading || disabled}
          >
            <option value="cash">Cash</option>
            <option value="bank">Bank Transfer</option>
            <option value="card">Card</option>
            <option value="other">Other</option>
            {creditsAvailable?.available_credit > 0 && (
              <option value="overpayment_credit">
                Overpayment Credit ({creditsAvailable.currency}
                {creditsAvailable.available_credit.toLocaleString()} available)
              </option>
            )}
          </select>
        </div>
      </div>

      {!isCreditMethod ? (
        <div className="application-payment-form__receipt">
          <label className="form-label">Payment Receipt</label>
          {!receiptFile ? (
            <div
              className="application-payment-form__dropzone"
              onDrop={onDrop}
              onDragOver={onDragOver}
              onClick={() => document.getElementById(uploadInputId)?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  document.getElementById(uploadInputId)?.click();
                }
              }}
            >
              <p className="text-muted mb-0">Drag & drop or click to upload</p>
              <input
                id={uploadInputId}
                type="file"
                accept={RECEIPT_FILE_ACCEPT}
                className="d-none"
                onChange={(e) => onReceiptChange?.(e.target.files?.[0])}
              />
            </div>
          ) : (
            <div className="application-payment-form__file">
              {isReceiptImageFile(receiptFile) && receiptPreview ? (
                <img
                  src={receiptPreview}
                  alt="Selected receipt"
                  className="application-payment-form__file-thumb"
                />
              ) : isReceiptPdfFile(receiptFile) ? (
                <div className="application-payment-form__file-thumb application-payment-form__file-thumb--pdf">
                  PDF
                </div>
              ) : null}
              <div className="application-payment-form__file-meta">
                <p className="mb-0">{receiptFile.name}</p>
                <p className="text-muted mb-0 small">
                  {(receiptFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <button
                type="button"
                className="application-payment-form__file-remove"
                onClick={onReceiptRemove}
                aria-label="Remove receipt"
              >
                <FaTrash />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="application-payment-form__credit-note">
          No receipt needed — using client&apos;s overpayment credit.
        </div>
      )}

      <div
        className={`application-payment-form__actions${
          onCancel ? " application-payment-form__actions--split" : ""
        }`}
      >
        {onCancel && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary"
          onClick={onSubmit}
          disabled={!canSubmit}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Processing...
            </>
          ) : (
            <>
              <FaDollarSign size={14} className="me-2" />
              Add Payment
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default ApplicationPaymentForm;
